// client/src/pages/wholesaler/Login.js
// ============================================
// WHOLESALER LOGIN PAGE
// Updated: Orange Theme & Correct Translation Keys
// ============================================

import React, { useState, useEffect } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PasswordField from '../../components/common/PasswordField';

const WholesalerLogin = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { loginWholesaler, isAuthenticated, user, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [redirect, setRedirect] = useState(false);

  // Redirect if already logged in as wholesaler
  useEffect(() => {
    if (isAuthenticated && user?.role === 'wholesaler') {
      setRedirect(true);
    }
  }, [isAuthenticated, user]);

  // Handle input change
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { success, error: loginError, accountStatus, rejectionReason, companyName } = await loginWholesaler(formData);

      if (success) {
        setRedirect(true);
      } else {
        if (accountStatus && accountStatus !== 'approved') {
          navigate('/account-pending', {
            state: {
              accountStatus,
              rejectionReason,
              companyName,
              userType: 'wholesaler'
            }
          });
        } else {
          setError(loginError);
        }
      }
    } catch (err) {
      const response = err.response?.data;
      if (response?.accountStatus && response.accountStatus !== 'approved') {
        navigate('/account-pending', {
          state: {
            accountStatus: response.accountStatus,
            rejectionReason: response.rejectionReason,
            companyName: response.companyName,
            userType: 'wholesaler'
          }
        });
      } else {
        setError(err.message || t('auth:login.errors.invalidCredentials'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (redirect) {
    const redirectTo = location.state?.from?.pathname || '/wholesaler/dashboard';
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <Navbar />

      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        {/* Title: Σύνδεση Χονδρέμπορου */}
        <h1 className="text-3xl font-bold text-orange-600 mb-2">
          {t('auth:login.wholesalerTitle')}
        </h1>
        {/* Subtitle: Πρόσβαση στον πίνακα ελέγχου... */}
        <p className="text-gray-600">
          {t('auth:login.wholesalerSubtitle')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('auth:login.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder={t('auth:login.emailPlaceholder')}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('auth:login.password')}
              </label>
              <PasswordField
                id="password"
                name="password"
                required
                value={formData.password}
                onChange={handleChange}
                placeholder={t('auth:login.passwordPlaceholder')}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link 
                  to="/forgot-password" 
                  className="font-medium text-orange-600 hover:text-orange-500"
                >
                  {t('auth:login.forgotPassword')}
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
              >
                {loading ? <LoadingSpinner size="sm" /> : t('auth:login.button')}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  {t('auth:login.noAccount')}
                </span>
              </div>
            </div>

            <div className="mt-6 text-center">
              {/* "Νέος Χονδρέμπορος; Εγγραφείτε εδώ" */}
              <p className="text-sm text-gray-600 mb-2">
                {t('auth:login.noWholesalerAccount')}
              </p>
              <Link
                to="/wholesaler/register"
                className="font-medium text-orange-600 hover:text-orange-500"
              >
                {t('auth:login.registerLink')}
              </Link>
            </div>

            <div className="mt-6 text-center pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-2">Are you a Business?</p>
              <Link
                to="/login"
                className="text-sm font-medium text-gray-600 hover:text-orange-600 transition-colors"
              >
                 {/* Link to Business Login */}
                {t('auth:login.businessTitle')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default WholesalerLogin;